import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { v4 as uuidv4 } from 'uuid';

import { LoadManagementService, FilteredLoad, LoadDetail, FilterLoadsResponse, FilteredSaleOrder } from '../../services/load-management.service';
import { SalesOrderService, GetSalesOrderByIdResponse, SalesOrderDetail } from '../../services/sales-order';
import { SalesFlowService, DeliverInvoiceItem, FilteredSaleOrderWithInvoice, GetSalesLoadResponse } from '../../services/sales-flow-service';
import { Customer, CustomerService } from '../../services/customer';
import { SelectedProduct, Product, ProductService, normalizeSaleItems, normalizeSalePrice } from '../../services/products';
import { Pricelist, PricelistService, getPrice } from '../../services/price-lists';
import { MessageDialogService } from '../../services/message-dialog';
import { NotificationService } from '../../services/notification';

import { LoadTableComponent } from '../../components/load-table-component/load-table-component';
import { PackupOrdersListComponent } from '../../components/packup-orders-list-component/packup-orders-list-component';
import { CartComponent } from '../../components/cart-component/cart-component';
import { ProductListComponent } from '../../components/product-list-component/product-list-component';

@Component({
  selector: 'app-pack-up',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatTooltipModule,
    LoadTableComponent,
    PackupOrdersListComponent,
    CartComponent,
    ProductListComponent
  ],
  templateUrl: './pack-up-component.html',
  styleUrls: ['./pack-up-component.scss']
})
export class PackUpComponent {
  // Filters / pagination for loads
  startDate: string = '';
  endDate: string = '';
  page: number = 1;
  pageSize: number = 10;
  totalPages: number = 1;

  // Loads view
  loads: FilteredLoad[] = [];
  selectedLoad: LoadDetail | null = null;

  // Orders within a load
  loadOrders: FilteredSaleOrderWithInvoice[] = [];

  // Active order editing
  selectedCustomer: Customer | null = null;
  selectedProducts: SelectedProduct[] = [];
  products: Product[] = [];
  productsMode = false; // when true, show product add panel similar to return/product list
  selectedOrderDetail: SalesOrderDetail | null = null;
  
  // UI state
  view: 'loads' | 'orders' | 'cart' = 'loads';
  isReadonly: boolean = false;
  allItemsSelected: boolean = false;
  isViewingInvoice: boolean = false;

  constructor(
    private loadService: LoadManagementService,
    private salesOrderService: SalesOrderService,
    private salesFlowService: SalesFlowService,
    private customerService: CustomerService,
    private productService: ProductService,
    private pricelistService: PricelistService,
    private messageDialog: MessageDialogService,
    private notificationService: NotificationService
  ) {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    this.startDate = today.toISOString().split('T')[0];
    this.endDate = tomorrow.toISOString().split('T')[0];
  }

  // -------- Loads --------
  fetchLoads(): void {
    if (!this.startDate || !this.endDate) {
      this.messageDialog.warning('Please select start and end dates.');
      return;
    }
    const payload = { startDate: this.startDate, endDate: this.endDate, page: this.page, page_size: this.pageSize };
    this.loadService.filterLoads(payload).subscribe((res: FilterLoadsResponse) => {
      if (res.status === 'success' && res.loads) {
        this.loads = res.loads;
        this.totalPages = res.pages ?? 1;
        this.view = 'loads';
      } else {
        this.loads = [];
        this.totalPages = 1;
      }
    });
  }

  prevPage(): void {
    if (this.page > 1) {
      this.page--;
      this.fetchLoads();
    }
  }

  nextPage(): void {
    if (this.page < this.totalPages) {
      this.page++;
      this.fetchLoads();
    }
  }

  // Double-click handler from load table
  viewLoadDetails(load: FilteredLoad): void {
    this.salesFlowService.getSalesLoad({ load_id: load.id }).subscribe(res => {
      if (res.status === 'success' && res.load) {
        this.selectedLoad = res.load;
        this.loadOrders = [...(res.load.saleOrders || [])].map(o => ({ ...o, selected: false } as any));
        this.view = 'orders';
      } else {
        this.messageDialog.error(res.message || 'Unable to load details');
      }
    });
  }

  // -------- Orders in load --------
  onOrdersChange(updated: FilteredSaleOrderWithInvoice[]): void {
    this.loadOrders = updated;
  }

  openSelectedOrder(): void {
    const sel = this.loadOrders.find(o => !!o.selected);
    if (!sel) {
      this.messageDialog.warning('Please select an order to open');
      return;
    }
    this.openOrder(sel);
  }

  openOrder(order: FilteredSaleOrderWithInvoice): void {
    // Check if order is already invoiced
    if (order.hasPostedInvoice) {
      // Use getInvoicesBySalesOrderId for invoiced orders
      this.salesOrderService.getDeliveredBySalesOrderId(order.id).subscribe(async (resp: GetSalesOrderByIdResponse) => {
        if (resp.status !== 'success' || !resp.order) {
          this.messageDialog.error(resp.message || 'Unable to fetch invoice details');
          return;
        }
        await this.prepareCartFromInvoice(resp.order, true); // true for readonly mode
        this.view = 'cart';
      });
      return;
    }

    this.salesOrderService.getSalesOrderById(order.id).subscribe(async (resp: GetSalesOrderByIdResponse) => {
      if (resp.status !== 'success' || !resp.order) {
        this.messageDialog.error(resp.message || 'Unable to fetch order details');
        return;
      }
      await this.prepareCartFromOrder(resp.order, false); // false for normal mode
      this.view = 'cart';
    });
  }

  private async prepareCartFromOrder(order: SalesOrderDetail, readonly: boolean = false): Promise<void> {
    const customers = await this.customerService.get().toPromise();
    this.selectedOrderDetail = order; 
    this.selectedCustomer = (customers || []).find(c => c.id === order.customerId) || null;
    this.selectedProducts = (order.lines || []).map(l => ({
      productId: l.product_id,
      quantity: l.quantity,
      price: l.price_unit
    }));
    // Load products with customer-specific pricing for search panel
    this.productService.get().subscribe(products => {
      this.pricelistService.get().subscribe(pricelists => {
        const matching = pricelists.find(p => p.id === this.selectedCustomer?.priceListId) as Pricelist | undefined;
        this.products = products.map(prod => ({ ...prod, list_price: normalizeSalePrice(prod, getPrice(matching, prod)) } as Product));
      });
    });
    this.productsMode = false;
    this.isReadonly = readonly;
    this.isViewingInvoice = false;
    // Reset selection state whenever we open a cart
    this.allItemsSelected = false;
  }

  private async prepareCartFromInvoice(invoice: SalesOrderDetail, readonly: boolean = true): Promise<void> {
    const customers = await this.customerService.get().toPromise();
    this.selectedOrderDetail = invoice; 
    this.selectedCustomer = (customers || []).find(c => c.id === invoice.customerId) || null;
    this.selectedProducts = (invoice.lines || []).map(l => ({
      productId: l.product_id,
      quantity: l.quantity,
      price: l.price_unit
    }));
    // Load products with customer-specific pricing for search panel
    this.productService.get().subscribe(products => {
      this.pricelistService.get().subscribe(pricelists => {
        const matching = pricelists.find(p => p.id === this.selectedCustomer?.priceListId) as Pricelist | undefined;
        this.products = products.map(prod => ({ ...prod, list_price: normalizeSalePrice(prod, getPrice(matching, prod)) } as Product));
      });
    });
    this.productsMode = false;
    this.isReadonly = readonly; // Always readonly for invoices
    this.isViewingInvoice = true;
    // Reset selection state whenever we open a cart
    this.allItemsSelected = false;
  }

  // -------- Cart / products editing --------
  onCartUpdated(updated: SelectedProduct[]): void {
    this.selectedProducts = updated;
  }

  editOrder(): void {
    this.productsMode = true;
  }

  // -------- Pack & Invoice --------
  packAndInvoice(): void {
    // If viewing invoice details, show info message
    if (this.isViewingInvoice) {
      this.messageDialog.info('You are viewing invoice details. This order has already been invoiced.');
      return;
    }

    if (!this.selectedLoad) {
      this.messageDialog.error('No load selected');
      return;
    }
    const orderId = this.selectedOrderDetail?.order_id;
    if (!orderId) {
      this.messageDialog.warning('Please select an order to invoice');
      return;
    }
    // Enforce selection of all items when selection is enabled (non-readonly mode)
    if (!this.isReadonly && !this.allItemsSelected) {
      this.messageDialog.warning('Please select all items before continuing.');
      return;
    }

    this.selectedProducts = normalizeSaleItems(this.selectedProducts, this.products);

    const items: DeliverInvoiceItem[] = this.selectedProducts
      .filter(p => p.quantity > 0)
      .map(p => ({ product_id: p.productId, quantity: p.quantity, price_unit: p.price }));
    if (items.length === 0) {
      this.messageDialog.error('Cart is empty. Add at least one product.');
      return;
    }
    this.messageDialog.confirm('Review items and quantities before creating the invoice. Proceed?', 'Confirm Pack & Invoice')
      .subscribe((confirmed: boolean) => {
        if (!confirmed) return;
        const idempotencyKey = `deliver-${Date.now()}-${uuidv4()}`;
        this.salesFlowService.deliverAndPost(orderId, idempotencyKey, items, { confirm: true, post: true })
          .subscribe({
            next: res => {
              if (res.status === 'success') {
                const inv = res.invoice_name || `#${res.invoice_id}`;
                this.notificationService.showMessage(`Packed and invoiced: ${inv}`);
                // Reload the selected load to update order statuses
                this.reloadSelectedLoad();
                this.view = 'orders';
              } else {
                this.messageDialog.error(res.message || 'Failed to pack & invoice');
              }
            },
            error: err => {
              if (err?.status === 400 && err?.error?.code === 'minimum_price_violation') {
                this.messageDialog.error('Product pricing changed. Sync products and try again.');
                return;
              }
              this.messageDialog.error(err?.error?.message || err?.message || 'Failed to pack & invoice');
            }
          });
      });
  }

  // -------- Selection handling from cart --------
  onSelectionStateChange(allSelected: boolean): void {
    this.allItemsSelected = allSelected;
  }

  private reloadSelectedLoad(): void {
    if (!this.selectedLoad) return;
    
    this.salesFlowService.getSalesLoad({ load_id: this.selectedLoad.id }).subscribe(res => {
      if (res.status === 'success' && res.load) {
        this.selectedLoad = res.load;
        this.loadOrders = [...(res.load.saleOrders || [])].map(o => ({ ...o, selected: false } as any));
      }
    });
  }

  // -------- Reset Load to Draft --------
  resetLoadToDraft(): void {
    if (!this.selectedLoad) {
      this.messageDialog.error('No load selected');
      return;
    }

    if (this.selectedLoad.state !== 'loaded') {
      this.messageDialog.warning('Only loaded loads can be reset to draft');
      return;
    }

    this.messageDialog.confirm(
      `Are you sure you want to reset load "${this.selectedLoad.name}" back to draft? This will allow editing the load again.`,
      'Confirm Reset to Draft'
    ).subscribe((confirmed: boolean) => {
      if (!confirmed) return;

      const payload = {
        load_id: this.selectedLoad!.id,
        write_date: this.selectedLoad!.write_date
      };

      this.salesFlowService.resetLoadedLoadToDraft(payload).subscribe(res => {
        if (res.status === 'success') {
          this.notificationService.showMessage(`Load "${this.selectedLoad!.name}" has been reset to draft`);
          // Reload the selected load to update its state
          this.reloadSelectedLoad();
        } else {
          this.messageDialog.error(res.message || 'Failed to reset load to draft');
        }
      });
    });
  }
}


